// src/prototype/data/execute/approvalWorkflow.ts
// ============================================================
// APPROVAL WORKFLOW (Oct 21, v3): personas for "Acting as", per-step checklist templates and
// default owners, and the pure resolution of step state from the initiative record + edits.
// ============================================================
import type { InitiativeRecord } from '../executionInitiatives';

export interface Persona { id: string; name: string; role: string }

/** Demo personas. Role-based except the client's own persona (Dana). */
export const PERSONAS: Persona[] = [
  { id: 'dana', name: 'Dana Whitfield', role: 'VP Analytics, initiative owner' },
  { id: 'sponsor', name: 'Executive sponsor', role: 'COO' },
  { id: 'cfo', name: 'CFO', role: 'Finance' },
  { id: 'cmo', name: 'CMO', role: 'Clinical leadership' },
  { id: 'governance', name: 'Governance committee chair', role: 'Board governance' },
  { id: 'pmo', name: 'PMO lead', role: 'Enterprise PMO' },
];
export const DEFAULT_PERSONA_ID = 'dana';
export const personaById = (id: string | undefined) => PERSONAS.find((p) => p.id === id) ?? PERSONAS[0];
export const actorLabel = (p: Persona) => `${p.name} (${p.role})`;

export interface StepTemplate { items: string[]; ownerId: string }

/** Checklist template by step, matched on the step label. */
export function templateFor(label: string, cfo?: boolean): StepTemplate {
  const l = label.toLowerCase();
  if (cfo || l.includes('cfo')) return { ownerId: 'cfo', items: [
    'Base, conservative and aggressive scenarios reviewed',
    'Flagged assumptions resolved or accepted',
    'Funding source and budget line identified',
    'CFO Summary exported and attached',
  ] };
  if (l.includes('sponsor')) return { ownerId: 'sponsor', items: [
    'Executive sponsor named',
    'Sponsor reviewed the decision brief',
    'Initiative owner assigned',
  ] };
  if (l.includes('business case') || l.includes('roi')) return { ownerId: 'dana', items: [
    'ROI model reviewed in DVA',
    'Implementation cost estimate attached',
    'Benefit owner identified',
    'Assumption Appendix exported',
  ] };
  if (l.includes('board') || l.includes('governance') || l.includes('committee') || l.includes('ratif')) return { ownerId: 'governance', items: [
    'Board Rationale Packet distributed',
    'Alternatives considered documented',
    'Risks and mitigations reviewed',
    'Committee vote recorded',
  ] };
  if (l.includes('kickoff') || l.includes('kick-off') || l.includes('launch')) return { ownerId: 'pmo', items: [
    'PMO owner assigned',
    'Kickoff meeting scheduled',
    'Measurement baseline agreed',
    'Handoff kit sent to the PMO',
  ] };
  return { ownerId: 'dana', items: ['Materials reviewed', 'Open questions resolved'] };
}

export type SignoffDecision = 'approved' | 'deferred' | 'declined';
export interface Signoff {
  approver: string;
  role: string;
  date: string; // yyyy-mm-dd
  decision: SignoffDecision;
  rationale: string;
  reversal?: string;
}
export interface Tick { done: boolean; by: string; at: string }
export interface StepState {
  ownerId?: string;
  due?: string; // yyyy-mm-dd
  checks?: Record<string, Tick>;
  decision?: Signoff;
  /** A step completed in the initiative record, reopened by the user. */
  reopened?: boolean;
}

export type StepStatus = 'done' | 'current' | 'pending' | 'blocked';
export interface ResolvedStep {
  index: number;
  label: string;
  cfo?: boolean;
  note?: string;
  status: StepStatus;
  /** True when "done" comes from the initiative record rather than a decision recorded here. */
  fromRecord: boolean;
  decision?: Signoff;
  ownerId: string;
  due?: string;
  items: { label: string; done: boolean; by?: string; at?: string }[];
  checklistComplete: boolean;
  overdue: boolean;
}

export function todayIso(now: Date = new Date()): string {
  const d = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 10);
}

/**
 * Merge the record's step states with the user's edits. A recorded decision wins; a record
 * step marked done stays done unless reopened (its checklist then reads as complete, from the
 * record); the first open step is current; anything after a defer/decline is pending.
 */
export function resolveSteps(record: InitiativeRecord, steps: Record<number, StepState>, now: Date = new Date()): ResolvedStep[] {
  let blocked = false;
  let currentAssigned = false;
  const today = todayIso(now);
  return record.approval.map((step, i) => {
    const st = steps[i] ?? {};
    const tpl = templateFor(step.label, step.cfo);
    const fromRecordDone = step.state === 'done' && !st.reopened;
    const items = tpl.items.map((label) => {
      const t = st.checks?.[label];
      if (t) return { label, done: t.done, by: t.by, at: t.at };
      return fromRecordDone ? { label, done: true, by: 'Initiative record' } : { label, done: false };
    });
    const checklistComplete = items.every((x) => x.done);
    let status: StepStatus;
    if (blocked) status = 'pending';
    else if (st.decision) {
      status = st.decision.decision === 'approved' ? 'done' : 'blocked';
      if (status === 'blocked') blocked = true;
    } else if (fromRecordDone) status = 'done';
    else if (!currentAssigned) { status = 'current'; currentAssigned = true; }
    else status = 'pending';
    return {
      index: i,
      label: step.label,
      cfo: step.cfo,
      note: step.note,
      status,
      fromRecord: status === 'done' && !st.decision,
      decision: st.decision,
      ownerId: st.ownerId ?? tpl.ownerId,
      due: st.due,
      items,
      checklistComplete,
      overdue: Boolean(st.due && st.due < today && status !== 'done'),
    };
  });
}
